import { __ } from '@/hub-shared/utils/i18n';

export const LIFECYCLE_STEP_KEYS = [
  'lancement',
  'completion',
  'supervision',
  'cloture',
  'signature',
  'archivage',
] as const;

export type LifecycleStepKey = typeof LIFECYCLE_STEP_KEYS[number];

/**
 * Fallback progress-bar phase from stored mission status.
 * Backend `lifecycle.progress_step_key` is authoritative when present.
 */
export const resolveProgressBarStepKey = (
  storedStatus: string | undefined,
  linkedReportId: number | null,
  signingAvailable = false,
): LifecycleStepKey => {
  if (storedStatus === 'signee' || storedStatus === 'archivee') {
    return 'archivage';
  }
  if (storedStatus === 'cloturee' || storedStatus === 'en_audit' || storedStatus === 'auditee') {
    return signingAvailable ? 'signature' : 'archivage';
  }
  if (storedStatus === 'supervisee' || storedStatus === 'generee') {
    return 'cloture';
  }
  if (storedStatus === 'completee') {
    return 'supervision';
  }
  return linkedReportId !== null ? 'completion' : 'lancement';
};

/** @deprecated Use {@link resolveProgressBarStepKey}. */
export const resolveCurrentStepKey = resolveProgressBarStepKey;

/**
 * Step key that drives the primary mission action button (may differ from the progress bar).
 */
export const resolveMissionActionStepKey = (
  storedStatus: string | undefined,
  linkedReportId: number | null,
  progressStepKey?: LifecycleStepKey,
  signingAvailable = false,
): LifecycleStepKey | null => {
  if (storedStatus === 'signee' || storedStatus === 'archivee') {
    return storedStatus === 'archivee' ? null : 'archivage';
  }
  if (storedStatus === 'cloturee' || storedStatus === 'en_audit' || storedStatus === 'auditee') {
    return signingAvailable ? 'signature' : 'archivage';
  }
  if (storedStatus === 'generee') {
    return progressStepKey ?? 'cloture';
  }
  if (storedStatus === 'supervisee') {
    return 'cloture';
  }
  if (storedStatus === 'completee') {
    return 'supervision';
  }
  if (storedStatus === 'en_cours' || storedStatus === undefined) {
    return linkedReportId !== null ? 'completion' : null;
  }
  return linkedReportId !== null ? 'completion' : 'lancement';
};

type StepActionCandidate = {
  action: string;
  label: () => string;
};

const STEP_PRIMARY_ACTION_CANDIDATES: Record<LifecycleStepKey, StepActionCandidate[] | null> = {
  lancement: null,
  completion: [
    { action: 'resume_report', label: () => __('Reprendre le rapport') },
  ],
  supervision: [
    { action: 'open_work_folder', label: () => __('Reprendre la supervision') },
    { action: 'review_diligences', label: () => __('Reprendre la supervision') },
  ],
  cloture: [
    { action: 'close', label: () => __('Clôturer') },
  ],
  signature: [
    { action: 'sign', label: () => __('Signer') },
  ],
  archivage: [
    { action: 'archive', label: () => __('Archiver') },
  ],
};

const LIFECYCLE_TRANSITION_ACTIONS = new Set(['close', 'sign', 'archive']);

export type MissionProgressPrimaryAction = {
  action: string;
  label: string;
};

export const resolveMissionProgressPrimaryAction = (
  stepKey: LifecycleStepKey,
): MissionProgressPrimaryAction | null => {
  const candidates = STEP_PRIMARY_ACTION_CANDIDATES[stepKey];
  if (candidates === null || candidates.length === 0) {
    return null;
  }

  const primary = candidates[0];
  return {
    action: primary.action,
    label: primary.label(),
  };
};

export const resolveApplicableProgressActionKey = (
  stepKey: LifecycleStepKey,
  allowedActions: string[] | undefined,
): string | null => {
  const candidates = STEP_PRIMARY_ACTION_CANDIDATES[stepKey];
  if (candidates === null || candidates.length === 0) {
    return null;
  }

  const allowed = allowedActions ?? [];
  const permitted = candidates.find((candidate) => allowed.includes(candidate.action));
  return permitted?.action ?? candidates[0].action;
};

export const isMissionProgressActionAllowed = (
  action: string | null,
  allowedActions: string[] | undefined,
): boolean => action !== null && (allowedActions ?? []).includes(action);

export const shouldShowMissionProgressAction = (
  actionStepKey: LifecycleStepKey | null,
  readOnly: boolean,
  storedStatus?: string,
): boolean => {
  if (actionStepKey === null) {
    return false;
  }
  if (actionStepKey === 'lancement') {
    return false;
  }
  if (actionStepKey === 'archivage' && storedStatus === 'archivee') {
    return false;
  }

  const primaryAction = resolveMissionProgressPrimaryAction(actionStepKey);
  if (primaryAction === null) {
    return false;
  }

  if (LIFECYCLE_TRANSITION_ACTIONS.has(primaryAction.action)) {
    return true;
  }

  return !readOnly;
};
